import { differenceWith, uniqBy } from 'lodash'
import * as React from 'react'
import Flatpickr from 'react-flatpickr'

import 'flatpickr/dist/flatpickr.css'
import loadTranslation from 'flatpickr_locales/locale_resolver'
import { useTranslation } from 'react-i18next'
import { LOCIZE_NAMESPACE as NAMESPACE } from '../../common'

import { Container } from './DatePicker.styles'

interface IProps {
  dates: Date[]
  onDateSelection: (selectedDate: Date) => void
  disabled: boolean
  minDate?: Date
}

function compareTwoDates(firstDate: Date, secondDate: Date) {
  return firstDate.getTime() === secondDate.getTime()
}

function toTime(date: Date): number {
  return date.getTime()
}

function DatePicker(props: IProps) {
  const {
    i18n: { resolvedLanguage },
  } = useTranslation(NAMESPACE)
  const selectedLang = loadTranslation(resolvedLanguage)
  const { dates, onDateSelection, disabled, minDate } = props

  const minDateString = minDate ? minDate.toISOString() : 'today'

  return (
    <Container>
      <Flatpickr
        value={dates}
        options={{
          inline: true,
          minDate: minDateString,
          mode: 'multiple',
          monthSelectorType: 'static',
          locale: selectedLang,
        }}
        onChange={(selectedDates, _currentDateString, instance) => {
          if (disabled) {
            instance.setDate(dates)
            return
          }

          // User selected same date twice or thrice
          if (dates.length > selectedDates.length) {
            const differentDates = differenceWith(
              uniqBy(dates, toTime),
              uniqBy(selectedDates, toTime),
              compareTwoDates,
            )

            // Selected for a third time the same date
            if (differentDates.length === 0) {
              onDateSelection(selectedDates[0])
            } else {
              onDateSelection(differentDates[0])
            }
            return
          }

          // User added a new date
          if (selectedDates.length > dates.length) {
            const lastDate = selectedDates.slice().pop()
            if (lastDate) {
              onDateSelection(lastDate)
            }
            return
          }
        }}
        onDayCreate={(selectedDates, _currentDateString, _self, day) => {
          const isCurrentDaySelected = selectedDates.find(
            date => date.getTime() === day.dateObj.getTime(),
          )
          if (isCurrentDaySelected) {
            day.innerHTML += "<span class='event'></span>"
          }
        }}
      />
    </Container>
  )
}

export default React.memo(DatePicker)
